import React from 'react';
import { hotelInfo } from '../mockData';
import { useSEO } from '../hooks/useSEO';

const PrivacyPolicyPage = () => {
  useSEO({
    title: 'Privacy Policy | OlivOak Villa',
    description: 'Learn how OlivOak Villa collects, uses, and protects your personal information. Read our full privacy policy.'
  });

  return (
    <main className="min-h-screen" style={{ backgroundColor: 'var(--bg-primary)' }}>
      {/* Hero */}
      <section
        className="py-20"
        style={{ backgroundColor: 'var(--bg-secondary)' }}
      >
        <div className="container mx-auto px-6 md:px-10 text-center">
          <h1 className="heading-1 mb-4">Privacy Policy</h1>
          <p className="body-large" style={{ color: 'var(--text-secondary)' }}>
            Your privacy matters to us at {hotelInfo.name}
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="section-padding">
        <div className="container mx-auto px-6 md:px-10" style={{ maxWidth: '800px' }}>
          <div className="space-y-10">
            <div>
              <p className="body-small mb-2" style={{ color: 'var(--text-light)' }}>
                Last updated: March 2026
              </p>
              <p className="body-base" style={{ color: 'var(--text-secondary)' }}>
                {hotelInfo.name} ("we", "our", or "us") is committed to protecting your personal information
                and your right to privacy. This Privacy Policy explains how we collect, use, disclose, and
                safeguard your information when you visit our website or make a reservation with us.
              </p>
            </div>

            <div>
              <h2 className="heading-3 mb-3">1. Information We Collect</h2>
              <p className="body-base mb-3" style={{ color: 'var(--text-secondary)' }}>
                We collect information that you voluntarily provide to us when you:
              </p>
              <ul className="list-disc pl-6 space-y-2 body-base" style={{ color: 'var(--text-secondary)' }}>
                <li>Make a reservation or booking enquiry</li>
                <li>Create an account on our website</li>
                <li>Contact us via our contact form, email, or phone</li>
                <li>Leave a review or testimonial</li>
                <li>Subscribe to our newsletter or marketing communications</li>
              </ul>
              <p className="body-base mt-3" style={{ color: 'var(--text-secondary)' }}>
                This information may include your name, email address, phone number, postal address,
                payment details, identification documents, and any other information you choose to provide.
              </p>
            </div>

            <div>
              <h2 className="heading-3 mb-3">2. How We Use Your Information</h2>
              <p className="body-base mb-3" style={{ color: 'var(--text-secondary)' }}>
                We use the information we collect for the following purposes:
              </p>
              <ul className="list-disc pl-6 space-y-2 body-base" style={{ color: 'var(--text-secondary)' }}>
                <li>Processing and managing your reservations</li>
                <li>Communicating with you about your booking, including confirmations and updates</li>
                <li>Processing payments securely</li>
                <li>Providing customer support and responding to enquiries</li>
                <li>Improving our services and website experience</li>
                <li>Sending promotional communications (only with your consent)</li>
                <li>Complying with legal obligations</li>
              </ul>
            </div>

            <div>
              <h2 className="heading-3 mb-3">3. Information Sharing</h2>
              <p className="body-base" style={{ color: 'var(--text-secondary)' }}>
                We do not sell, trade, or rent your personal information to third parties. We may share
                your information only with trusted service providers who assist us in operating our website,
                processing payments, or delivering services to you — and only to the extent necessary for
                them to perform those services. All third-party providers are contractually obligated to
                keep your information confidential.
              </p>
            </div>

            <div>
              <h2 className="heading-3 mb-3">4. Payment Security</h2>
              <p className="body-base" style={{ color: 'var(--text-secondary)' }}>
                All payment transactions are processed through secure, PCI-compliant payment gateways.
                We do not store your full credit card or bank details on our servers. Payment information
                is encrypted during transmission using industry-standard SSL/TLS technology.
              </p>
            </div>

            <div>
              <h2 className="heading-3 mb-3">5. Cookies and Tracking</h2>
              <p className="body-base" style={{ color: 'var(--text-secondary)' }}>
                Our website may use cookies and similar technologies to enhance your browsing experience,
                analyse site traffic, and understand where our visitors come from. You can control cookie
                preferences through your browser settings. Disabling cookies may affect some functionality
                of our website.
              </p>
            </div>

            <div>
              <h2 className="heading-3 mb-3">6. Data Retention</h2>
              <p className="body-base" style={{ color: 'var(--text-secondary)' }}>
                We retain your personal information only for as long as necessary to fulfil the purposes
                outlined in this policy, or as required by law. Booking records are retained for a
                reasonable period after your stay for accounting and legal compliance purposes.
              </p>
            </div>

            <div>
              <h2 className="heading-3 mb-3">7. Your Rights</h2>
              <p className="body-base mb-3" style={{ color: 'var(--text-secondary)' }}>
                You have the right to:
              </p>
              <ul className="list-disc pl-6 space-y-2 body-base" style={{ color: 'var(--text-secondary)' }}>
                <li>Access the personal data we hold about you</li>
                <li>Request correction of inaccurate information</li>
                <li>Request deletion of your personal data (subject to legal requirements)</li>
                <li>Withdraw consent for marketing communications at any time</li>
                <li>Lodge a complaint with the relevant data protection authority</li>
              </ul>
            </div>

            <div>
              <h2 className="heading-3 mb-3">8. Children's Privacy</h2>
              <p className="body-base" style={{ color: 'var(--text-secondary)' }}>
                Our services are not directed to individuals under the age of 18. We do not knowingly
                collect personal information from children. If you believe we have inadvertently collected
                information from a child, please contact us so we can promptly remove it.
              </p>
            </div>

            <div>
              <h2 className="heading-3 mb-3">9. Changes to This Policy</h2>
              <p className="body-base" style={{ color: 'var(--text-secondary)' }}>
                We may update this Privacy Policy from time to time to reflect changes in our practices
                or legal requirements. Any updates will be posted on this page with a revised "Last updated"
                date. We encourage you to review this policy periodically.
              </p>
            </div>

            <div>
              <h2 className="heading-3 mb-3">10. Contact Us</h2>
              <p className="body-base" style={{ color: 'var(--text-secondary)' }}>
                If you have any questions or concerns about this Privacy Policy, please contact us:
              </p>
              <div className="mt-3 space-y-1 body-base" style={{ color: 'var(--text-secondary)' }}>
                <p><strong style={{ color: 'var(--text-primary)' }}>Email:</strong> {hotelInfo.contact.email}</p>
                <p><strong style={{ color: 'var(--text-primary)' }}>Phone:</strong> {hotelInfo.contact.phone}</p>
                <p><strong style={{ color: 'var(--text-primary)' }}>Address:</strong> {hotelInfo.location.address}</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default PrivacyPolicyPage;
